/**
 * 模态焦点域与 owned portal 协议：按 React 所有权组合物理 DOM 与 body portal 的顺序焦点。
 */

import {
  type RefCallback,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
} from 'react';

const TABBABLE_ELEMENT_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[tabindex]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

interface OwnedPortal {
  anchorElement: HTMLElement;
  portalElement: HTMLElement;
}

export interface FocusScope {
  collectTabbableElements: (excludedPortalElement?: HTMLElement | null) => HTMLElement[];
  contains: (element: Node | null) => boolean;
  getFocusRestorationFallbackElement: (excludedNestedFocusScope?: FocusScope) => HTMLElement | null;
  isTopmostFocusScope: () => boolean;
  isTopmostRegisteredNestedFocusScope: (nestedFocusScope: FocusScope) => boolean;
  registerNestedFocusScope: (nestedFocusScope: FocusScope) => () => void;
  registerOwnedPortal: (anchorElement: HTMLElement, portalElement: HTMLElement) => () => void;
}

const FocusScopeContext = createContext<FocusScope | null>(null);

export const FocusScopeProvider = FocusScopeContext.Provider;

function isFirstSummaryInDetails(element: HTMLElement): boolean {
  if (element.tagName !== 'SUMMARY') return true;
  const detailsElement = element.parentElement;
  if (detailsElement?.tagName !== 'DETAILS') return false;
  return (
    Array.from(detailsElement.children).find((child) => child.tagName === 'SUMMARY') === element
  );
}

function isHiddenByClosedDetails(element: HTMLElement): boolean {
  let ancestorElement = element.parentElement;
  while (ancestorElement) {
    if (ancestorElement.tagName === 'DETAILS' && !ancestorElement.hasAttribute('open')) {
      const firstSummary = Array.from(ancestorElement.children).find(
        (child) => child.tagName === 'SUMMARY',
      );
      if (!firstSummary?.contains(element)) return true;
    }
    ancestorElement = ancestorElement.parentElement;
  }
  return false;
}

function getContentEditableState(element: HTMLElement): boolean | undefined {
  const value = element.getAttribute('contenteditable');
  if (value === null) return undefined;
  const normalizedValue = value.toLowerCase();
  if (
    normalizedValue === '' ||
    normalizedValue === 'true' ||
    normalizedValue === 'plaintext-only'
  ) {
    return true;
  }
  if (normalizedValue === 'false') return false;
  return undefined;
}

function isEffectivelyContentEditable(element: HTMLElement | null): boolean {
  let currentElement = element;
  while (currentElement) {
    const state = getContentEditableState(currentElement);
    if (state !== undefined) return state;
    currentElement = currentElement.parentElement;
  }
  return false;
}

function isContentEditingHost(element: HTMLElement): boolean {
  return (
    isEffectivelyContentEditable(element) && !isEffectivelyContentEditable(element.parentElement)
  );
}

function getSequentialTabIndex(element: HTMLElement): number {
  if (element.hasAttribute('tabindex')) return element.tabIndex;
  if (isContentEditingHost(element)) return 0;
  const isMediaControl =
    (element.tagName === 'AUDIO' || element.tagName === 'VIDEO') &&
    element.hasAttribute('controls');
  if (isMediaControl) return 0;
  return element.tabIndex;
}

function isHiddenByCss(element: HTMLElement): boolean {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    const computedStyle = window.getComputedStyle(currentElement);
    if (
      computedStyle.display === 'none' ||
      computedStyle.visibility === 'hidden' ||
      computedStyle.visibility === 'collapse'
    ) {
      return true;
    }
    currentElement = currentElement.parentElement;
  }
  return false;
}

function isElementTabbable(element: HTMLElement, excludeAriaHidden: boolean): boolean {
  if (element.matches(':disabled, input[type="hidden"]')) return false;
  if (
    element.closest(
      excludeAriaHidden ? '[hidden], [inert], [aria-hidden="true"]' : '[hidden], [inert]',
    )
  ) {
    return false;
  }
  if (
    !isFirstSummaryInDetails(element) ||
    isHiddenByClosedDetails(element) ||
    isHiddenByCss(element)
  ) {
    return false;
  }
  if (element.hasAttribute('tabindex') && element.tabIndex < 0) return false;
  return getSequentialTabIndex(element) >= 0;
}

function keepOneTabStopPerRadioGroup(elements: HTMLElement[]): HTMLElement[] {
  const radioGroupsByForm = new Map<HTMLFormElement | Document, Map<string, HTMLInputElement[]>>();
  for (const element of elements) {
    if (!(element instanceof HTMLInputElement) || element.type !== 'radio' || !element.name) {
      continue;
    }
    const formOwner = element.form ?? element.ownerDocument;
    const groupsByName = radioGroupsByForm.get(formOwner) ?? new Map<string, HTMLInputElement[]>();
    const radioGroup = groupsByName.get(element.name) ?? [];
    radioGroup.push(element);
    groupsByName.set(element.name, radioGroup);
    radioGroupsByForm.set(formOwner, groupsByName);
  }

  const radioTabStops = new Set<HTMLInputElement>();
  for (const groupsByName of radioGroupsByForm.values()) {
    for (const radioGroup of groupsByName.values()) {
      const checkedRadio = radioGroup.find((radio) => radio.checked);
      const radioTabStop = checkedRadio ?? radioGroup[0];
      if (radioTabStop) radioTabStops.add(radioTabStop);
    }
  }

  return elements.filter(
    (element) =>
      !(element instanceof HTMLInputElement) ||
      element.type !== 'radio' ||
      !element.name ||
      radioTabStops.has(element),
  );
}

function sortInSequentialFocusOrder(elements: HTMLElement[]): HTMLElement[] {
  return keepOneTabStopPerRadioGroup(elements)
    .map((element, logicalOrder) => ({
      element,
      logicalOrder,
      tabIndex: getSequentialTabIndex(element),
    }))
    .sort((first, second) => {
      const firstHasPositiveTabIndex = first.tabIndex > 0;
      const secondHasPositiveTabIndex = second.tabIndex > 0;
      if (firstHasPositiveTabIndex !== secondHasPositiveTabIndex) {
        return firstHasPositiveTabIndex ? -1 : 1;
      }
      if (
        firstHasPositiveTabIndex &&
        secondHasPositiveTabIndex &&
        first.tabIndex !== second.tabIndex
      ) {
        return first.tabIndex - second.tabIndex;
      }
      return first.logicalOrder - second.logicalOrder;
    })
    .map(({ element }) => element);
}

export function collectDocumentTabbableElements(
  excludedElement: HTMLElement | null,
): HTMLElement[] {
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>(TABBABLE_ELEMENT_SELECTOR),
  ).filter((element) => !excludedElement?.contains(element) && isElementTabbable(element, false));
  return sortInSequentialFocusOrder(elements);
}

export function createFocusScope(
  rootElementRef: RefObject<HTMLElement | null>,
  parentFocusScope: FocusScope | null = null,
): FocusScope {
  const ownedPortals = new Set<OwnedPortal>();
  const nestedFocusScopes: FocusScope[] = [];

  const focusScope: FocusScope = {
    collectTabbableElements(excludedPortalElement) {
      const rootElement = rootElementRef.current;
      if (!rootElement) return [];
      const ownedPortalsByAnchor = new Map<HTMLElement, OwnedPortal[]>();
      for (const ownedPortal of ownedPortals) {
        const portalsForAnchor = ownedPortalsByAnchor.get(ownedPortal.anchorElement) ?? [];
        portalsForAnchor.push(ownedPortal);
        ownedPortalsByAnchor.set(ownedPortal.anchorElement, portalsForAnchor);
      }

      const tabbableElements: HTMLElement[] = [];
      const visitedElements = new Set<HTMLElement>();
      const visitedPortalElements = new Set<HTMLElement>();
      const visitElement = (element: HTMLElement) => {
        if (visitedElements.has(element) || excludedPortalElement?.contains(element)) return;
        visitedElements.add(element);
        if (element.matches(TABBABLE_ELEMENT_SELECTOR) && isElementTabbable(element, true)) {
          tabbableElements.push(element);
        }
        for (const ownedPortal of ownedPortalsByAnchor.get(element) ?? []) {
          const portalElement = ownedPortal.portalElement;
          if (
            !portalElement.isConnected ||
            visitedPortalElements.has(portalElement) ||
            excludedPortalElement?.contains(portalElement)
          ) {
            continue;
          }
          visitedPortalElements.add(portalElement);
          visitElement(portalElement);
          for (const descendant of portalElement.querySelectorAll<HTMLElement>('*')) {
            visitElement(descendant);
          }
        }
      };

      visitElement(rootElement);
      for (const descendant of rootElement.querySelectorAll<HTMLElement>('*')) {
        visitElement(descendant);
      }
      return sortInSequentialFocusOrder(tabbableElements);
    },
    contains(element) {
      const rootElement = rootElementRef.current;
      if (rootElement?.contains(element)) return true;
      for (const ownedPortal of ownedPortals) {
        if (ownedPortal.portalElement.contains(element)) return true;
      }
      return false;
    },
    getFocusRestorationFallbackElement(excludedNestedFocusScope) {
      let topmostNestedFocusScope: FocusScope | undefined;
      for (
        let nestedFocusScopeIndex = nestedFocusScopes.length - 1;
        nestedFocusScopeIndex >= 0;
        nestedFocusScopeIndex -= 1
      ) {
        const nestedFocusScope = nestedFocusScopes[nestedFocusScopeIndex];
        if (nestedFocusScope !== excludedNestedFocusScope) {
          topmostNestedFocusScope = nestedFocusScope;
          break;
        }
      }
      return (
        topmostNestedFocusScope?.getFocusRestorationFallbackElement() ?? rootElementRef.current
      );
    },
    isTopmostFocusScope() {
      if (nestedFocusScopes.length > 0) return false;
      return parentFocusScope
        ? parentFocusScope.isTopmostRegisteredNestedFocusScope(focusScope)
        : true;
    },
    isTopmostRegisteredNestedFocusScope(nestedFocusScope) {
      if (nestedFocusScopes.at(-1) !== nestedFocusScope) return false;
      return parentFocusScope
        ? parentFocusScope.isTopmostRegisteredNestedFocusScope(focusScope)
        : true;
    },
    registerNestedFocusScope(nestedFocusScope) {
      const existingIndex = nestedFocusScopes.indexOf(nestedFocusScope);
      if (existingIndex >= 0) nestedFocusScopes.splice(existingIndex, 1);
      nestedFocusScopes.push(nestedFocusScope);
      return () => {
        const registeredIndex = nestedFocusScopes.indexOf(nestedFocusScope);
        if (registeredIndex >= 0) nestedFocusScopes.splice(registeredIndex, 1);
      };
    },
    registerOwnedPortal(anchorElement, portalElement) {
      const ownedPortal = { anchorElement, portalElement };
      ownedPortals.add(ownedPortal);
      return () => ownedPortals.delete(ownedPortal);
    },
  };
  return focusScope;
}

export function useFocusScope(): FocusScope | null {
  return useContext(FocusScopeContext);
}

export function useOwnedFocusScopePortalRef<T extends HTMLElement>(
  anchorElementRef: RefObject<HTMLElement | null>,
  forwardedPortalElementRef?: { current: T | null },
  restoreFocusToAnchorOnUnmount = false,
): RefCallback<T> {
  const focusScope = useFocusScope();
  const unregisterPortalRef = useRef<(() => void) | null>(null);
  const portalElementRef = useRef<T | null>(null);

  const setPortalElement = useCallback(
    (portalElement: T | null) => {
      const previousPortalElement = portalElementRef.current;
      if (!portalElement && previousPortalElement && restoreFocusToAnchorOnUnmount) {
        const activeElement = document.activeElement;
        const focusBelongsToPortal =
          activeElement instanceof Node && previousPortalElement.contains(activeElement);
        const focusIsUnclaimed =
          !(activeElement instanceof HTMLElement) ||
          activeElement === document.body ||
          activeElement === document.documentElement ||
          !activeElement.isConnected;
        const anchorElement = anchorElementRef.current;
        if ((focusBelongsToPortal || focusIsUnclaimed) && anchorElement?.isConnected) {
          anchorElement.focus();
        }
      }
      unregisterPortalRef.current?.();
      unregisterPortalRef.current = null;
      portalElementRef.current = portalElement;
      if (forwardedPortalElementRef) forwardedPortalElementRef.current = portalElement;
      const anchorElement = anchorElementRef.current;
      if (focusScope && anchorElement && portalElement) {
        unregisterPortalRef.current = focusScope.registerOwnedPortal(anchorElement, portalElement);
      }
    },
    [anchorElementRef, focusScope, forwardedPortalElementRef, restoreFocusToAnchorOnUnmount],
  );

  useLayoutEffect(
    () => () => {
      unregisterPortalRef.current?.();
      unregisterPortalRef.current = null;
      portalElementRef.current = null;
    },
    [],
  );

  return setPortalElement;
}
